// Reusable IndexedDB-backed cache for Redux actions that fetch large
// collections from Firebase RTDB. Pattern: hydrate Redux from cache on
// cold start (no network), refresh in background after TTL or on
// explicit mutation.
//
// Why IndexedDB and not sessionStorage / localStorage:
//   sessionStorage caps at ~5–10 MB per origin. Production /users at
//   ~36k records is ~30 MB even after slim projection — well over quota.
//   IndexedDB has GB-level quotas (typically 50% of free disk).
//
// Why not redux-persist or similar:
//   This is a per-collection, per-action surgical cache. Adding a
//   persistence layer to all of Redux would persist things we don't want
//   surviving reloads (auth state, transient UI, etc.) and pull in a
//   meaningful dependency for what's ~200 lines of plain code.
//
// Mobile note: React Native has no window.indexedDB, so isIDBSupported()
// returns false and every cache call resolves to a no-op. The mobile
// flow is byte-identical to before this helper existed.

const isIDBSupported = () =>
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const _dbPromiseByName = {};

const openDB = (dbName, storeName, version) => {
  if (!isIDBSupported()) return Promise.resolve(null);
  const cacheKey = `${dbName}::${storeName}::${version}`;
  if (_dbPromiseByName[cacheKey]) return _dbPromiseByName[cacheKey];

  _dbPromiseByName[cacheKey] = new Promise((resolve) => {
    const req = window.indexedDB.open(dbName, version);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.warn(`[idbCache:${dbName}] open failed:`, req.error && req.error.message);
      resolve(null);
    };
  });
  return _dbPromiseByName[cacheKey];
};

/**
 * Create a cache instance bound to a database + store + TTL.
 *
 * @param {string} dbName  IDB database name (one DB per logical collection)
 * @param {string} storeName  Object store name (allows multi-key sub-caches)
 * @param {number} defaultTtlMs  Cache freshness window in ms
 * @returns {{
 *   read:        (key: string, label?: string) => Promise<any|null>,
 *   write:       (key: string, value: any, label?: string) => Promise<boolean>,
 *   patch:       (key: string, mutator: (current: any|null) => any|null) => Promise<void>,
 *   invalidate:  (key: string) => Promise<void>,
 *   invalidateAll: () => Promise<void>,
 * }}
 */
export const createIDBCache = (dbName, storeName, defaultTtlMs) => {
  const dbVersion = 1;

  const idbGet = async (key) => {
    const db = await openDB(dbName, storeName, dbVersion);
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readonly');
        const req = tx.objectStore(storeName).get(key);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      } catch (_) { resolve(null); }
    });
  };

  const idbPut = async (key, value) => {
    const db = await openDB(dbName, storeName, dbVersion);
    if (!db) return false;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).put(value, key);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => {
          console.warn(`[idbCache:${dbName}] write failed:`, tx.error && tx.error.message);
          resolve(false);
        };
      } catch (_) { resolve(false); }
    });
  };

  const idbDelete = async (key) => {
    const db = await openDB(dbName, storeName, dbVersion);
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch (_) { resolve(); }
    });
  };

  const idbClear = async () => {
    const db = await openDB(dbName, storeName, dbVersion);
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch (_) { resolve(); }
    });
  };

  return {
    read: async (key, label) => {
      const tag = label || `${dbName}:${key}`;
      const entry = await idbGet(key);
      if (!entry || !entry.cachedAt || entry.value === undefined) return null;
      if (Date.now() - entry.cachedAt > defaultTtlMs) {
        console.debug(`[${tag}] expired — refetching`);
        return null;
      }
      const size = Array.isArray(entry.value) ? entry.value.length : '1 entry';
      console.debug(`[${tag}] HIT — hydrating ${size} from IndexedDB (no network)`);
      return entry.value;
    },

    write: async (key, value, label) => {
      const tag = label || `${dbName}:${key}`;
      const ok = await idbPut(key, { cachedAt: Date.now(), value });
      if (ok) {
        const size = Array.isArray(value) ? value.length : '1 entry';
        console.debug(`[${tag}] WROTE ${size} to IndexedDB`);
      }
      return ok;
    },

    // mutator: (current) => next. Pass-through for current=null means
    // the caller can decide whether to seed or skip on missing entries.
    patch: async (key, mutator) => {
      const entry = await idbGet(key);
      const current = entry && entry.value !== undefined ? entry.value : null;
      const next = mutator(current);
      if (next === undefined || next === null) return;
      await idbPut(key, { cachedAt: entry ? entry.cachedAt : Date.now(), value: next });
    },

    invalidate: idbDelete,
    invalidateAll: idbClear,
  };
};
